/*
 * Drives every control on the Dynamic Finance Page.
 *
 * There are four pieces of state - provider, category, size and term - and one render pass that derives everything else from them. Nothing is fetched: the section serialises every provider's rates into `[data-finance-providers]` and each card carries its own variant lookup, so a selection change is pure arithmetic.
 *
 * The provider controls deliberately live in two places (the top bar's tablist and the calculator's radiogroup) over one shared value, so both stay in step whichever one is used.
 *
 * The selected provider is also mirrored into the query string, so it survives a reload and can be linked to directly.
 *
 * The markup ships rendered for the initial selection, so the page is complete and correct before this runs. The maths and the disclaimer substitution match what Liquid did for that first paint - see snippets/dynamic-finance-page-calculator.liquid and snippets/finance-monthly-payment.liquid.
 */

const PROVIDER = '[data-finance-provider]';
const PROVIDER_DETAILS = '[data-finance-provider-details]';
const PROVIDER_CONTENT = '[data-finance-provider-content]';
const PROVIDER_VARIANT = '[data-finance-provider-variant]';
const CATEGORY = '[data-finance-category]';
const CATEGORY_PANEL = '[data-finance-category-panel]';
const SIZE = '[data-finance-size]';
const SIZE_WRAPPER = '[data-finance-size-wrapper]';
const TERMS = '[data-finance-terms]';
const TERM = '[data-finance-term]';
const CARD = '[data-finance-card]';
const GROUP = '[role="tablist"], [role="radiogroup"]';

/*
 * The selected provider is mirrored into the query string, so the choice survives a reload and a link can land someone on a particular provider - `?finance=v12`.
 *
 * The value is a provider's `identifier`, which is also what section blocks are tagged with, so a URL a merchant writes by hand matches what they typed in the theme editor.
 */
const PROVIDER_PARAM = 'finance';

/*
 * Liquid's `handle` filter, near enough: provider keys are already handleized on the way out, so this only really has to forgive whatever casing or spacing turns up in a hand-written URL.
 */
const handleize = value =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/*
 * `Finance` carries the amortisation maths and the money formatting, and is published on `window.radiant` by Theme.js. This file is a plain deferred asset rather than part of the bundle, so it can't import it and can't assume radiant.js has run yet - hence the poll. Capped so a page where the bundle failed settles into "controls still switch, figures stay as rendered" instead of spinning.
 */
const financeLib = () =>
  new Promise(resolve => {
    if (window.radiant?.finance) {
      resolve(window.radiant.finance);
      return;
    }

    const started = Date.now();
    const poll = setInterval(() => {
      if (window.radiant?.finance || Date.now() - started > 5000) {
        clearInterval(poll);
        resolve(window.radiant?.finance ?? null);
      }
    }, 50);
  });

class DynamicFinancePage extends HTMLElement {
  connectedCallback() {
    this.providerControls = Array.from(this.querySelectorAll(PROVIDER));

    if (!this.providerControls.length) return;

    this.categoryTabs = Array.from(this.querySelectorAll(CATEGORY));
    this.cards = Array.from(this.querySelectorAll(CARD));
    this.providers = this.#readProviders();
    this.finance = financeLib();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('change', this.#onChange);

    // Mirror what the markup already says rather than imposing a fresh default on it
    this.dataset.activeProvider =
      this.#checked(this.providerControls)?.dataset.financeProvider ?? '';
    this.dataset.activeCategory = this.#checked(this.categoryTabs)?.dataset.financeCategory ?? '';

    // A provider named in the URL outranks the one the markup opened on. Anything unrecognised is ignored rather than blanking the page, and `selectProvider` renders as part of switching, so only the untouched path needs its own render.
    const requested = this.#providerFromUrl();

    if (requested && requested !== this.activeProvider) {
      this.selectProvider(requested);
    } else {
      this.#render();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('change', this.#onChange);
  }

  get activeProvider() {
    return this.dataset.activeProvider || null;
  }

  get activeCategory() {
    return this.dataset.activeCategory || null;
  }

  /**
   * The term group belonging to the active provider - the terms on offer differ per provider, so each has its own group and only one is ever visible.
   */
  get activeTermGroup() {
    return this.querySelector(`[data-finance-terms="${this.activeProvider}"]`);
  }

  /**
   * The plan the selected pill stands for.
   *
   * Attributes are strings, and the engines branch on `interest === 0` and type-check `loadFactor`, so everything is coerced. A missing attribute becomes `NaN`, which the guards in `Finance` read as "no bound" for the thresholds and as "can't quote this" for a load factor.
   */
  get activeTerm() {
    const pill = this.#checked(Array.from(this.activeTermGroup?.querySelectorAll(TERM) ?? []));

    if (!pill) return null;

    const read = key => {
      const value = pill.dataset[key];

      return value == null || value === '' ? NaN : Number(value);
    };

    return {
      term: read('financeTerm'),
      interest: read('financeInterest'),
      // The amounts this plan lends between, in pence - both move from term to term within the same provider, so they belong to the pill rather than to the provider
      minThreshold: read('financeMinThreshold'),
      maxThreshold: read('financeMaxThreshold'),
      // Snap only: the monthly instalment per £1 of credit
      loadFactor: read('financeLoadFactor'),
      // Snap's mandatory £1, say - only the representative example reduces the credit by it
      deposit: read('financeDeposit'),
    };
  }

  /**
   * @param {string} handle - Provider metaobject handle
   * @param {Object} [options]
   * @param {boolean} [options.focus] - Move focus to the control that now owns the selection
   * @returns {void}
   */
  selectProvider(handle, { focus = false } = {}) {
    const controls = this.providerControls.filter(el => el.dataset.financeProvider === handle);

    if (!controls.length) return;

    if (handle !== this.activeProvider) {
      this.dataset.activeProvider = handle;

      this.providerControls.forEach(el => {
        this.#setChecked(el, el.dataset.financeProvider === handle);
      });

      // `active` rather than `hide` for the top bar's bullets: on desktop every provider's bullets are on show at once and only the highlight moves, so the CSS owns that decision
      this.#toggleByHandle(PROVIDER_DETAILS, 'financeProviderDetails', handle, 'active');
      this.#toggleByHandle(TERMS, 'financeTerms', handle, 'hide');
      this.#toggleByHandle('[data-finance-disclaimer]', 'financeDisclaimer', handle, 'hide');
      // Whole content blocks that belong to one provider. Blocks the merchant left untagged carry no attribute at all, so they never match this and simply stay on screen.
      this.#toggleByHandle(PROVIDER_CONTENT, 'financeProviderContent', handle, 'hidden');
      // Per-provider variants *within* a block that itself always shows - the FAQs, where both providers have their own set. Anything with a group per provider can use this.
      this.#toggleByHandle(PROVIDER_VARIANT, 'financeProviderVariant', handle, 'hide');

      // The top bar's tabs label the content region, so the label has to follow the selection
      const content = this.querySelector('[data-finance-content]');
      const tab = controls.find(el => el.getAttribute('role') === 'tab');

      if (content && tab?.id) content.setAttribute('aria-labelledby', tab.id);

      this.#writeProviderToUrl(handle);
      this.#refreshCarousels();
      this.#render();
      this.dispatchEvent(
        new CustomEvent('finance-provider:change', { bubbles: true, detail: { provider: handle } })
      );
    }

    if (focus) {
      // Whichever group the keystroke came from is the one that should keep focus
      const focused = controls.find(el => el.closest(GROUP)?.contains(document.activeElement));
      (focused ?? controls[0]).focus();
    }
  }

  /**
   * @param {string} handle - Category metaobject handle
   * @param {Object} [options]
   * @param {boolean} [options.focus] - Move focus to the newly selected tab
   * @returns {void}
   */
  selectCategory(handle, { focus = false } = {}) {
    const tab = this.categoryTabs.find(el => el.dataset.financeCategory === handle);

    if (!tab) return;

    if (handle !== this.activeCategory) {
      const previousSize = this.#sizeFor(this.activeCategory);

      this.dataset.activeCategory = handle;

      this.categoryTabs.forEach(el => {
        this.#setChecked(el, el.dataset.financeCategory === handle);
      });

      this.#toggleByHandle(CATEGORY_PANEL, 'financeCategoryPanel', handle, 'hidden');
      this.#toggleByHandle(SIZE_WRAPPER, 'financeSizeWrapper', handle, 'hide');

      // Carry the chosen size across where the new category also stocks it, so switching between mattress types doesn't silently drop the customer back to the smallest size
      const select = this.querySelector(`[data-finance-size="${handle}"]`);

      if (
        select &&
        previousSize &&
        Array.from(select.options).some(o => o.value === previousSize)
      ) {
        select.value = previousSize;
      }

      this.#refreshCarousels();
      this.#render();
      this.dispatchEvent(
        new CustomEvent('finance-category:change', { bubbles: true, detail: { category: handle } })
      );
    }

    if (focus) tab.focus();
  }

  /**
   * @param {HTMLElement} pill - The term control to select
   * @param {Object} [options]
   * @param {boolean} [options.focus] - Move focus to it
   * @returns {void}
   */
  selectTerm(pill, { focus = false } = {}) {
    const group = pill.closest(GROUP);

    if (!group) return;

    group.querySelectorAll(TERM).forEach(el => this.#setChecked(el, el === pill));

    this.#render();

    if (focus) pill.focus();
  }

  /**
   * The provider the query string asks for, matched forgivingly against the controls on the page.
   *
   * @returns {string|null} The matching control's key, or null when the param is absent or names a provider this page doesn't offer.
   */
  #providerFromUrl() {
    const requested = new URLSearchParams(window.location.search).get(PROVIDER_PARAM);

    if (!requested) return null;

    const wanted = handleize(requested);

    return (
      this.providerControls.find(el => handleize(el.dataset.financeProvider) === wanted)?.dataset
        .financeProvider ?? null
    );
  }

  #writeProviderToUrl(handle) {
    const url = new URL(window.location.href);

    // Nothing to do when the URL already says exactly this, so arriving on a well-formed deep link leaves the address alone. A loosely-written one - different casing, say - is matched by `#providerFromUrl` and then tidied to the canonical value here.
    if (url.searchParams.get(PROVIDER_PARAM) === handle) return;

    url.searchParams.set(PROVIDER_PARAM, handle);

    try {
      /*
       * `replaceState`, not `push`: this is a filter-style control, and one history entry per toggle would leave someone pressing Back several times to get off the page. It also keeps any other params - a `variant`, a campaign tag - because `URL` rewrites only the one key.
       */
      window.history.replaceState(window.history.state, '', url);
    } catch {
      // Browsers rate-limit history writes. Losing the address bar update is not worth breaking the
      // rest of the selection over.
    }
  }

  #readProviders() {
    const json = this.querySelector('[data-finance-providers]')?.textContent;

    if (!json) return {};

    try {
      return JSON.parse(json);
    } catch {
      return {};
    }
  }

  #sizeFor(category) {
    return this.querySelector(`[data-finance-size="${category}"]`)?.value ?? null;
  }

  #checked(elements) {
    return (
      elements.find(
        el =>
          el.getAttribute('aria-selected') === 'true' || el.getAttribute('aria-checked') === 'true'
      ) ?? elements[0]
    );
  }

  /**
   * Radios carry `aria-checked` and tabs carry `aria-selected`, so the same provider value can be
   * exposed correctly in both the top bar's tablist and the calculator's radiogroup.
   */
  #setChecked(el, isChecked) {
    const attribute = el.getAttribute('role') === 'radio' ? 'aria-checked' : 'aria-selected';

    el.setAttribute(attribute, isChecked ? 'true' : 'false');
    el.classList.toggle('is-active', isChecked);
    // Roving tabindex - only the selected control is in the tab order, arrows move between them
    el.tabIndex = isChecked ? 0 : -1;
  }

  /**
   * @param {string} selector - Matches one element per handle
   * @param {string} key - The dataset key holding each element's handle
   * @param {string} handle - The handle that should end up active
   * @param {'hidden'|'hide'|'active'} method - `hidden` for panels the browser should skip, `hide` to take the inactive ones out of the layout, `active` to only mark which one is current and leave visibility to the CSS.
   */
  /**
   * Re-measures every carousel on the page after a selection has changed what is on screen.
   *
   * Swiper works out its slide widths from the container's width at mount. Anything that ships `hidden` - a provider-scoped block for the provider the page didn't open on, a category panel that isn't the first - measures zero, so it mounts with no slide widths and a single pagination bullet, and stays that way once revealed because nothing tells it otherwise. `update()` is Swiper's own recalculation, and running it on reveal is what makes those carousels usable.
   *
   * Deliberately every carousel rather than only the ones inside the container just revealed: it is cheap, and it also covers the one that was *hidden* by this change, whose stale inline widths would otherwise be what it came back to.
   *
   * Reaches through `window.__ThemeComponents`, which Theme.js publishes, because this file is a plain deferred asset and can't import the Swiper component. Absent until radiant.js has mounted hence the guard - by the time a customer can click anything it is there.
   *
   * @returns {void}
   */
  #refreshCarousels() {
    const mounted = window.__ThemeComponents;

    if (!Array.isArray(mounted)) return;

    mounted.forEach(entry => {
      if (entry?.type !== 'swiper' || !this.contains(entry.elem)) return;

      entry.component?.update?.();
    });
  }

  #toggleByHandle(selector, key, handle, method) {
    this.querySelectorAll(selector).forEach(el => {
      const isActive = el.dataset[key] === handle;

      if (method === 'hidden') {
        el.hidden = !isActive;
        return;
      }

      el.classList.toggle('is-active', isActive);

      if (method === 'hide') el.classList.toggle('hide', !isActive);
    });
  }

  async #render() {
    const finance = await this.finance;

    if (!finance) return;

    /*
     * Normalised on the way in: the serialised rates arrive as JSON but the fields it carries are
     * only as clean as the metaobjects behind them, and the engines branch on `interest === 0` and
     * type-check `loadFactor`. `normaliseProvider` also carries the `key` the arithmetic is
     * dispatched on - each provider works its figures out differently and none can be derived from
     * another's APR.
     */
    const provider = finance.normaliseProvider(this.providers[this.activeProvider]);
    const rate = this.activeTerm;

    if (!provider || !rate) return;

    this.#renderApr(rate);

    // Cards in hidden panels are rendered too - each reads the size select of its own panel, so a
    // panel is already correct by the time its tab is clicked
    const prices = this.cards.map(card => this.#renderCard(card, provider, rate, finance));

    this.#renderDisclaimer(prices, finance, rate, provider);
  }

  #renderApr(rate) {
    const label = this.activeTermGroup?.querySelector('[data-finance-apr]');

    if (!label) return;

    // Matches the Liquid: two decimals for a real rate, a bare "0" for interest free
    const interest = Number.isFinite(rate.interest) ? rate.interest : 0;

    label.textContent = `${interest > 0 ? interest.toFixed(2) : '0'}% APR`;
  }

  /**
   * @returns {number|null} The card's price in pence, or null when it has no variant in the
   *   selected size - the disclaimer needs to know which cards actually counted.
   */
  #renderCard(card, provider, rate, finance) {
    const panel = card.closest(CATEGORY_PANEL);
    const category = panel?.dataset.financeCategoryPanel;
    const size = this.#sizeFor(category);

    let variants = {};

    try {
      variants = JSON.parse(card.dataset.financeCardVariants || '{}');
    } catch {
      variants = {};
    }

    const variant = variants[size];

    this.#swapLogo(card, provider);

    if (!variant) return null;

    /*
     * A card whose price falls outside what the selected plan lends between - or whose plan can't
     * be quoted with its own provider's method - shows no monthly figure at all, because there
     * isn't one the lender would honour. The price and the Shop Now link still update: the product
     * is on sale, it just isn't financeable on this plan.
     */
    const quote = finance.available(rate, variant.price)
      ? finance.quote(variant.price, rate, provider)
      : null;

    this.#setText(card, '[data-finance-card-price]', finance.formatMoney(variant.price));

    card.querySelector('[data-finance-card-finance]')?.classList.toggle('hide', !quote);

    if (quote) {
      this.#setText(card, '[data-finance-card-monthly]', finance.formatMoney(quote.monthly));
    }

    const compare = card.querySelector('[data-finance-card-compare]');

    if (compare) {
      const hasCompare = variant.compare > variant.price;

      compare.innerHTML = hasCompare ? finance.formatMoney(variant.compare) : '';
      compare.classList.toggle('hide', !hasCompare);
    }

    card.querySelectorAll('[data-finance-card-link]').forEach(link => {
      link.href = variant.url;
    });

    return variant.price;
  }

  #swapLogo(card, provider) {
    const logo = card.querySelector('[data-finance-card-logo]');

    if (logo && provider.logo) {
      logo.src = provider.logo;
      logo.alt = provider.name;

      // Keep the intrinsic ratio honest - providers' logos are different shapes
      if (provider.logoWidth > 0 && provider.logoHeight > 0) {
        const height = Number(logo.getAttribute('height')) || 24;

        logo.width = Math.round((provider.logoWidth / provider.logoHeight) * height);
        logo.height = height;
      }

      return;
    }

    this.#setText(card, '[data-finance-card-provider-name]', provider.name);
  }

  /**
   * The worked example beneath the controls: the cheapest card in the active category, at the selected size, over the selected term - so every figure in the copy agrees with the figures on the cards above it.
   *
   * A rate only lends above its own minimum spend, so where the selected size prices the cheapest card under it there is no agreement to describe: the example gives way to the message saying this plan length isn't open at this price. Liquid makes the same call for the first paint - see snippets/dynamic-finance-page-calculator.liquid.
   *
   * Re-substitutes the provider's untouched copy each time so repeated renders can't compound.
   */
  #renderDisclaimer(prices, finance, rate, provider) {
    const handle = this.activeProvider;
    const group = this.querySelector(`[data-finance-disclaimer="${handle}"]`);
    const template = this.querySelector(
      `[data-finance-disclaimer-template="${handle}"]`
    )?.textContent;

    if (!group || !rate) return;

    const target = group.querySelector('[data-finance-disclaimer-text]');
    const unavailable = group.querySelector('[data-finance-unavailable]');

    const inCategory = this.cards
      .map((card, index) => ({ card, price: prices[index] }))
      .filter(
        ({ card, price }) =>
          price != null &&
          card.closest(CATEGORY_PANEL)?.dataset.financeCategoryPanel === this.activeCategory
      );

    if (!inCategory.length) return;

    const price = Math.min(...inCategory.map(({ price: cardPrice }) => cardPrice));
    // The rate itself may require its own deposit up front (Snap's £1, say) - money advanced but
    // never borrowed, so the worked example runs on what's left once that's taken off the top.
    // The cards above still quote the full price - see Finance.quoteWithDeposit.
    const result = finance.quoteWithDeposit(price, rate, provider);
    const quote = result?.quote ?? null;
    const deposit = result?.deposit ?? 0;
    const credit = Math.max(0, price - deposit);

    target?.classList.toggle('hide', !quote);
    unavailable?.classList.toggle('hide', Boolean(quote));

    // Nothing the customer could take out at this price, and so no example to keep in step either
    if (!quote || !target || !template) return;

    const values = {
      CASH_PRICE: finance.formatAmount(price),
      DEPOSIT: finance.formatAmount(deposit),
      CREDIT: finance.formatAmount(credit),
      MONTHS: String(quote.term),
      MONTHLY_REPAYMENT: finance.formatAmount(quote.monthly),
      FINAL_PAYMENT: finance.formatAmount(quote.final),
      REPAYMENT_CLAUSE: finance.describeRepayments(quote),
      APR: finance.formatRate(quote.interest),
      // The annual rate of interest the provider discloses, which is not the APR and is not
      // derived from it - see the engines in lib/Finance
      ANNUAL_INTEREST_RATE: finance.formatRate(quote.annualRate),
      TOTAL_REPAYABLE: finance.formatAmount(quote.total),
      TOTAL_INTEREST: finance.formatAmount(quote.totalInterest)
    };

    // Anything unrecognised is left alone rather than blanked out, so a typo in the metaobject copy shows up as itself instead of silently disappearing
    target.innerHTML = template.replace(/\[([A-Z_]+)\]/g, (token, key) => values[key] ?? token);
  }

  #setText(scope, selector, value) {
    const el = scope.querySelector(selector);

    if (el) el.innerHTML = value;
  }

  #onClick = event => {
    const provider = event.target.closest(PROVIDER);

    if (provider) {
      this.selectProvider(provider.dataset.financeProvider);
      return;
    }

    const category = event.target.closest(CATEGORY);

    if (category) {
      this.selectCategory(category.dataset.financeCategory);
      return;
    }

    const term = event.target.closest(TERM);

    if (term) this.selectTerm(term);
  };

  #onChange = event => {
    if (event.target.matches(SIZE)) this.#render();
  };

  #onKeydown = event => {
    const control = event.target.closest(`${PROVIDER}, ${CATEGORY}, ${TERM}`);
    const group = control?.closest(GROUP);

    if (!control || !group) return;

    // Scoped to the control's own group so arrowing through the calculator's financing options
    // never jumps into the top bar's tabs
    const peers = Array.from(group.querySelectorAll(`${PROVIDER}, ${CATEGORY}, ${TERM}`));
    const current = peers.indexOf(control);
    const last = peers.length - 1;
    let next;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current === 0 ? last : current - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        next = current === last ? 0 : current + 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();

    const target = peers[next];

    if (target.dataset.financeProvider) {
      this.selectProvider(target.dataset.financeProvider, { focus: true });
    } else if (target.dataset.financeCategory) {
      this.selectCategory(target.dataset.financeCategory, { focus: true });
    } else {
      this.selectTerm(target, { focus: true });
    }
  };
}

customElements.define('dynamic-finance-page', DynamicFinancePage);
